import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Skeleton } from '../../../components/ui/skeleton';
import { UserPlus } from 'lucide-react';
import RecruitmentTableRow from './RecruitmentTableRow';

interface Recruitment {
  id: number;
  department_id: number;
  position_id: number;
  number_of_positions: number;
  employment_type: string;
  status: string;
  budget?: number;
  expected_start_date?: string;
}

interface RecruitmentTableProps {
  recruitments: Recruitment[];
  loading: boolean;
  filterStatus: string;
  onFilterChange: (status: string) => void;
  getDepartmentName: (id: number) => string;
  getPositionName: (id: number) => string;
  onStatusChange: (rec: Recruitment, status: string) => void;
  onEdit: (rec: Recruitment) => void;
  onDelete: (id: number) => void;
}

export default function RecruitmentTable({
  recruitments,
  loading,
  filterStatus,
  onFilterChange,
  getDepartmentName,
  getPositionName,
  onStatusChange,
  onEdit,
  onDelete,
}: RecruitmentTableProps) {
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <CardTitle className="text-lg">Daftar Permintaan Rekrutmen</CardTitle>
          <Select value={filterStatus} onValueChange={onFilterChange}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Filter Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua</SelectItem>
              <SelectItem value="pending">Menunggu</SelectItem>
              <SelectItem value="approved">Disetujui</SelectItem>
              <SelectItem value="in_progress">Dalam Proses</SelectItem>
              <SelectItem value="completed">Selesai</SelectItem>
              <SelectItem value="rejected">Ditolak</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead>Departemen</TableHead>
                <TableHead>Posisi</TableHead>
                <TableHead>Jumlah</TableHead>
                <TableHead>Tipe</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array(5)
                  .fill(0)
                  .map((_, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Skeleton className="h-10 w-40" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-10 w-40" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-28" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-6 w-24" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-6 w-24" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-8 w-16" />
                      </TableCell>
                    </TableRow>
                  ))
              ) : recruitments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <UserPlus className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-gray-500">Tidak ada permintaan rekrutmen</p>
                  </TableCell>
                </TableRow>
              ) : (
                recruitments.map(rec => (
                  <RecruitmentTableRow
                    key={rec.id}
                    rec={rec}
                    getDepartmentName={getDepartmentName}
                    getPositionName={getPositionName}
                    onStatusChange={onStatusChange}
                    onEdit={onEdit}
                    onDelete={onDelete}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
